import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StatementMessagingMessageModel } from '../../core/interfaces/statement-messaging-run.model';

export interface MessageDataState {
  data: StatementMessagingMessageModel;
  errorMessage: string;
  isLoading: boolean;
  filteredByRunId: boolean; // Track if data is filtered by run ID
  sourceRunId?: string; // Track which run filtered the data
}

@Injectable({
  providedIn: 'root',
})
export class MessageDataService {
  private readonly initialState: MessageDataState = {
    data: { statementMessages: [] },
    errorMessage: '',
    isLoading: false,
    filteredByRunId: false,
    sourceRunId: undefined,
  };

  private readonly messageDataState$ = new BehaviorSubject<MessageDataState>(this.initialState);

  /**
   * Observable for message data state
   */
  get messageData$(): Observable<MessageDataState> {
    return this.messageDataState$.asObservable();
  }

  /**
   * Get current state value
   */
  get currentState(): MessageDataState {
    return this.messageDataState$.value;
  }

  /**
   * Set message data - for all messages (not filtered by run)
   * @param data Message data
   * @param force Force update even if data is empty
   */
  setMessageData(data: StatementMessagingMessageModel, force = false): void {
    // Don't clear data unless forced or there's actual new data
    if (!force && data.statementMessages.length === 0 && this.currentState.data.statementMessages.length > 0) {
      return;
    }
    this.messageDataState$.next({
      ...this.currentState,
      data,
      errorMessage: '',
      isLoading: false,
      filteredByRunId: false,
      sourceRunId: undefined,
    });
  }

  /**
   * Set messages filtered by run ID
   * @param data Message data filtered by run
   * @param runId The run ID that filtered these messages
   * @param errorMessage Optional error message
   */
  setMessagesByRunId(
    data: StatementMessagingMessageModel,
    runId: string,
    errorMessage = ''
  ): void {
    this.messageDataState$.next({
      ...this.currentState,
      data,
      errorMessage,
      isLoading: false,
      filteredByRunId: true,
      sourceRunId: runId,
    });
  }

  /**
   * Set error message
   */
  setError(errorMessage: string): void {
    this.messageDataState$.next({
      ...this.currentState,
      errorMessage,
      data: { statementMessages: [] },
      isLoading: false,
    });
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    this.messageDataState$.next({
      ...this.currentState,
      isLoading,
    });
  }

  /**
   * Update complete state
   */
  updateState(state: Partial<MessageDataState>): void {
    this.messageDataState$.next({
      ...this.currentState,
      ...state,
    });
  }

  /**
   * Clear all data (force clear)
   */
  clearData(): void {
    this.messageDataState$.next(this.initialState);
  }

  /**
   * Check if has data
   */
  hasData(): boolean {
    return this.currentState.data.statementMessages.length > 0;
  }

  /**
   * Check if data is filtered by run ID
   */
  isFilteredByRunId(): boolean {
    return this.currentState.filteredByRunId;
  }

  /**
   * Get source run ID (if filtered)
   */
  getSourceRunId(): string | undefined {
    return this.currentState.sourceRunId;
  }

  /**
   * Get current message count
   */
  getMessageCount(): number {
    return this.currentState.data.statementMessages.length;
  }
}
